import React from 'react';
import {
  Routes,
  Route,
  useLocation,
  Link,
} from 'react-router-dom';

import { useIntl } from 'react-intl';
import {
  SubNav,
  SubNavHeader,
  SubNavSections,
  SubNavSection,
  SubNavLink,
} from '@strapi/design-system';
import {
  useStrapiApp,
  Layouts,
  useRBAC,
  getFetchClient,
} from '@strapi/strapi/admin';
import { useQuery } from 'react-query';

import pluginPermissions from '../../permissions';
import pluginId from '../../helpers/pluginId';
import List from '../../screens/List';
import Overview from '../../screens/Overview';
import PatternsListPage from '../../screens/Patterns/ListPage';
import PatternsEditPage from '../../screens/Patterns/EditPage';
import PatternsCreatePage from '../../screens/Patterns/CreatePage';
import PageNotFound from '../../screens/404';
import { InjectedRoute } from '../../types/injection-zones';
import { WebtoolsAddonInfo } from '../../types/addons';
import { PRO_ADDONS } from '../../constants/pro-addons';
import LockedAddonMenuItem from '../../components/LockedAddonMenuItem';

const App = () => {
  const getPlugin = useStrapiApp('MyComponent', (state) => state.getPlugin);
  const {
    allowedActions: { canList, canPatterns, canOverview },
  } = useRBAC(pluginPermissions);
  const { formatMessage } = useIntl();

  const plugin = getPlugin(pluginId);

  const routerComponents = plugin?.getInjectedComponents('webtoolsRouter', 'route') as unknown as InjectedRoute[];

  const location = useLocation();
  const currentPath = location.pathname;

  const { get } = getFetchClient();
  const addonsQuery = useQuery('addons', async () => get<WebtoolsAddonInfo[]>('/webtools/info/addons'));

  const installedPluginNames = Object.values(addonsQuery.data?.data || {})
    .map((addon) => addon.info.name);

  // Strip npm scope: "@pluginpal/webtools-addon-redirects" → "webtools-addon-redirects"
  const getPluginName = (packageName: string) => {
    if (packageName.includes('/')) return packageName.split('/')[1];
    return packageName;
  };

  // Find locked addons (Pro addons that are NOT installed)
  const lockedAddons = PRO_ADDONS.filter(
    (addon) => !installedPluginNames.includes(getPluginName(addon.packageName)),
  );

  return (
    <Layouts.Root
      sideNav={(
        <SubNav>
          <SubNavHeader value="" label="Webtools" />
          <SubNavSections>
            <SubNavSection label="Core">
              {canOverview && (
                <SubNavLink tag={Link} to="/plugins/webtools" key="overview" className={currentPath === '/plugins/webtools' ? 'active' : ''}>
                  {formatMessage({ id: 'webtools.settings.page.overview.title', defaultMessage: 'Overview' })}
                </SubNavLink>
              )}
              {canList && (
                <SubNavLink tag={Link} to="/plugins/webtools/urls" key="list" className={currentPath.startsWith('/plugins/webtools/urls') ? 'active' : ''}>
                  {formatMessage({ id: 'webtools.settings.page.list.title', defaultMessage: 'All URLs' })}
                </SubNavLink>
              )}
              {canPatterns && (
                <SubNavLink tag={Link} to="/plugins/webtools/patterns" key="patterns" className={currentPath.startsWith('/plugins/webtools/patterns') ? 'active' : ''}>
                  {formatMessage({ id: 'webtools.settings.page.patterns.title', defaultMessage: 'URL patterns' })}
                </SubNavLink>
              )}
            </SubNavSection>
            {(routerComponents.length > 0 || lockedAddons.length > 0) && (
              <SubNavSection label="Addons">
                {/* Installed addons - existing functionality */}
                {routerComponents.map(({ path, label }) => label && (
                  <SubNavLink tag={Link} to={`/plugins/webtools${path}`} key={path} className={currentPath.startsWith(`/plugins/webtools${path}`) ? 'active' : ''}>
                    {label}
                  </SubNavLink>
                ))}

                {/* Locked Pro addons - new functionality */}
                {lockedAddons.map((addon) => (
                  <LockedAddonMenuItem key={addon.id} addon={addon} />
                ))}
              </SubNavSection>
            )}
          </SubNavSections>
        </SubNav>
      )}
    >
      <Routes>
        <Route path="/" element={<Overview />} />
        <Route path="/urls" element={<List />} />
        <Route path="/patterns" element={<PatternsListPage />} />
        <Route path="/patterns/new" element={<PatternsCreatePage />} />
        <Route path="/patterns/:id" element={<PatternsEditPage />} />
        {routerComponents.map(({ path, Component }) => (
          <Route key={path} path={path} element={<Component />} />
        ))}

        <Route path="*" element={<PageNotFound />} />
      </Routes>
    </Layouts.Root>
  );
};

export default App;
